import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { environment } from '@environments/environment';
import { BehaviorSubject, catchError, Observable, tap, throwError } from 'rxjs';
import { CustomerList } from 'src/app/service/customer.model';
import {
  Category,
  IssueFeedBackData,
  IssueFeedBackDataResponse,
  OrganList,
  Params,
  ServiceRequestInfo,
  TopsErrorFormData,
  TypesAndCategoriesResponse,
} from '../models/issue-feedback.interface';

@Injectable({
  providedIn: 'root',
})
export class IssuesFeedbackService {
  serviceRequestIssueFeedbackData = new BehaviorSubject<ServiceRequestInfo>(null);
  serviceRequestIssueFeedbackData$ = this.serviceRequestIssueFeedbackData.asObservable();
  issueDetailsData = new BehaviorSubject<any>(null);
  issueDetailsData$ = this.issueDetailsData.asObservable();

  constructor(private httpClient: HttpClient) {}

  updateServiceRequestIssueFeedbackData(data: ServiceRequestInfo): void {
    this.serviceRequestIssueFeedbackData.next(data);
  }

  clearServiceRequestIssueFeedbackData(): void {
    this.serviceRequestIssueFeedbackData.next(null);
  }

  getAssignedFeedbacks(): Observable<IssueFeedBackData[]> {
    const url = `${environment.apiHostV2}/feedbacks/assignedToMe`;
    return this.httpClient.get<IssueFeedBackData[]>(url).pipe(catchError((error: HttpErrorResponse) => throwError(() => error)));
  }
  getAssignedCountFeedbacks(): Observable<number> {
    const url = `${environment.apiHostV2}/feedbacks/countAssignedToMe`;
    return this.httpClient.get<number>(url).pipe(catchError((error: HttpErrorResponse) => throwError(() => error)));
  }
  getOpenFeedbacks(params: Params): Observable<IssueFeedBackDataResponse> {
    const url = `${environment.apiHostV2}/feedbacks/getOpen`;
    return this.httpClient
      .post<IssueFeedBackDataResponse>(url, params || {})
      .pipe(catchError((error: HttpErrorResponse) => throwError(() => error)));
  }
  getAllIsuuesFeedbacks(params: Params): Observable<IssueFeedBackDataResponse> {
    const url = `${environment.apiHostV2}/feedbacks/search`;
    return this.httpClient
      .post<IssueFeedBackDataResponse>(url, params || {})
      .pipe(catchError((error: HttpErrorResponse) => throwError(() => error)));
  }
  getTripNumbersForCustomer(customerId: string): Observable<{ id: string; name: string }[]> {
    const url = `${environment.apiUrl}/ServiceRequest/GetTripNumbersForCustomer/${customerId}`;
    return this.httpClient.get<{ id: string; name: string }[]>(url).pipe(catchError((error: HttpErrorResponse) => throwError(() => error)));
  }
  getCategorylist(): Observable<Category[]> {
    const url = `${environment.apiHostV2}/feedbacks/categories`;
    return this.httpClient.get<Category[]>(url).pipe(catchError((error: HttpErrorResponse) => throwError(() => error)));
  }
  getTOPSCategorylist(): Observable<Category[]> {
    const url = `${environment.apiHostV2}/TOPS/categories`;
    return this.httpClient.get<Category[]>(url).pipe(catchError((error: HttpErrorResponse) => throwError(() => error)));
  }

  getFinanceCategorylist(): Observable<Category[]> {
    const url = `${environment.apiHostV2}/feedbacks/financeCategories`;
    return this.httpClient.get<Category[]>(url).pipe(catchError((error: HttpErrorResponse) => throwError(() => error)));
  }

  getOrganList(): Observable<OrganList[]> {
    const url = `${environment.apiHostV2}/Tops/organs`;
    return this.httpClient.get<OrganList[]>(url).pipe(catchError((error: HttpErrorResponse) => throwError(() => error)));
  }
  getEmployeeList(): Observable<{ id: string; name: string }[]> {
    const url = `${environment.apiHost}/Member/InternalMembers`;
    return this.httpClient.get<{ id: string; name: string }[]>(url).pipe(catchError((error: HttpErrorResponse) => throwError(() => error)));
  }
  getCustomerList(): Observable<CustomerList[]> {
    const url = `${environment.apiHost}/Customer/GetAll`;

    return this.httpClient.get<CustomerList[]>(url).pipe(catchError((error: HttpErrorResponse) => throwError(() => error)));
  }

  getTypesAndCategories(): Observable<TypesAndCategoriesResponse[]> {
    const url = `${environment.apiHostV2}/feedbacks/typesAndCategories`;

    return this.httpClient.get<TypesAndCategoriesResponse[]>(url).pipe(catchError((error: HttpErrorResponse) => throwError(() => error)));
  }
  getTripIDList(): Observable<string[]> {
    const url = `${environment.apiUrl}/ServiceRequest/GetTripsBasedOnTripNumber`;

    return this.httpClient.get<string[]>(url).pipe(catchError((error: HttpErrorResponse) => throwError(() => error)));
  }

  createServiceRequestIssueFeedback(data: any, path: string): Observable<any> {
    const url = `${environment.apiHostV2}/feedbacks/${path}`;

    return this.httpClient.post<any>(url, data).pipe(catchError((error: HttpErrorResponse) => throwError(() => error)));
  }

  getFeedbackDetails(id: string): Observable<IssueFeedBackData> {
    const url = `${environment.apiHostV2}/feedbacks/${id}`;

    return this.httpClient.get<IssueFeedBackData>(url).pipe(
      tap((res) => {
        this.issueDetailsData.next(res);
      }),
      catchError((error: HttpErrorResponse) => throwError(() => error)),
    );
  }

  clearIssueDetailsData(): void {
    this.issueDetailsData.next(null);
  }

  updateReviewFeedback(payload: any): Observable<any> {
    const url = `${environment.apiHostV2}/feedbacks/reviewFeedback`;

    return this.httpClient.post<any>(url, payload).pipe(catchError((error: HttpErrorResponse) => throwError(() => error)));
  }
  closeIssue(payload: any): Observable<any> {
    const url = `${environment.apiHostV2}/feedbacks/closeIssue`;

    return this.httpClient.post<any>(url, payload).pipe(catchError((error: HttpErrorResponse) => throwError(() => error)));
  }
  reassignReview(payload: any): Observable<any> {
    const url = `${environment.apiHostV2}/feedbacks/reassignFeedback`;

    return this.httpClient.post<any>(url, payload).pipe(catchError((error: HttpErrorResponse) => throwError(() => error)));
  }

  createTopsErrorFeedback(data: TopsErrorFormData): Observable<string> {
    const url = `${environment.apiHostV2}/Tops`;

    return this.httpClient.post<string>(url, data).pipe(catchError((error: HttpErrorResponse) => throwError(() => error)));
  }
}
